// ── Date formatting ───────────────────────────────────────
export function formatDate(val) {
  if (!val) return "—";
  let ms = typeof val === "number" ? val : parseInt(val);
  if (isNaN(ms)) return val;
  // Detect Excel serial number (Lark stores some dates this way)
  // Excel serial: days since 1900-01-01, typically < 100000
  if (ms < 100000) {
    // Convert Excel serial to ms timestamp
    ms = (ms - 25569) * 86400 * 1000;
  }
  return new Date(ms).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

export function formatDateTime(val) {
  if (!val) return "—";
  let ms = typeof val === "number" ? val : parseInt(val);
  if (isNaN(ms)) return val;
  if (ms < 100000) {
    ms = (ms - 25569) * 86400 * 1000;
  }
  return new Date(ms).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
    timeZone: "Asia/Kolkata",
  });
}

// ── Global Config ───────────────────────────────────────────
export const STATUS_OPTIONS = [
  "⬜ Not Started", 
  "🟡 In Progress", 
  "✅ Done", 
  "🔴 Overdue"
];

// ── Status badge config ───────────────────────────────────
export function getStatusBadge(status) {
  if (!status) return { cls: "badge-gray", label: "—" };
  const s = String(status).toLowerCase();
  if (s.includes("overdue")) return { cls: "badge-danger", label: status };
  if (s.includes("done") || s.includes("complet")) return { cls: "badge-success", label: status };
  if (s.includes("progress")) return { cls: "badge-warning", label: status };
  if (s.includes("not started") || s.includes("⬜")) return { cls: "badge-gray", label: status };
  return { cls: "badge-gray", label: status };
}

export function getPriorityBadge(priority) {
  if (!priority) return { cls: "badge-gray", label: "—", color: "var(--text-tertiary)", bg: "var(--surface-3)" };
  const p = String(priority).toLowerCase();

  if (p === "high" || p === "important") {
    return { cls: "badge-danger", label: "⚡ High", color: "var(--danger)", bg: "var(--danger-muted)" };
  }
  if (p === "medium" || p === "normal") {
    return { cls: "badge-warning", label: "Medium", color: "var(--warning)", bg: "var(--warning-muted)" };
  }
  if (p === "low") {
    return { cls: "badge-info", label: "Low", color: "var(--info)", bg: "var(--info-muted)" };
  }

  // Fallback for unknown data
  return { cls: "badge-gray", label: priority, color: "var(--text-tertiary)", bg: "var(--surface-2)" };
}

// ── Extract display name from user field ──────────────────
export function getUserName(val) {
  if (!val) return "—";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === "string") return v;
      return v?.en_name || v?.name || v?.email || "?";
    }).join(", ");
  }
  if (typeof val === "object") {
    if (val.users) return val.users.map((u) => u.en_name || u.name || u.email).join(", ");
    return val.en_name || val.name || val.email || "?";
  }
  return String(val);
}

export function getUserEmail(val) {
  if (!val) return null;
  if (typeof val === "object" && !Array.isArray(val)) {
    if (val.email) return val.email;
    if (val.users?.[0]?.email) return val.users[0].email;
  }
  if (Array.isArray(val)) {
    return val[0]?.email || null;
  }
  return null;
}

// ── Check if a user in a field value (by userId or name) ──
export function userMatchesField(fieldVal, userOrName) {
  if (!fieldVal || !userOrName) return false;

  // userOrName can be a string (name) or object (JWT user with userId, name, email)
  const userId = typeof userOrName === "object" ? userOrName.userId : null;
  const name   = typeof userOrName === "object" 
    ? (userOrName.name || "").toLowerCase().trim()
    : (userOrName || "").toLowerCase().trim();

  const check = (v) => {
    if (!v) return false;
    // Match by Lark open_id first (most reliable)
    if (userId && typeof v === "object") {
      if (v.open_id === userId || v.id === userId) return true;
    }
    if (typeof v === "string") return name ? v.toLowerCase().includes(name) : false;
    if (typeof v === "object") {
      return (
        (v.en_name || "").toLowerCase().includes(name) ||
        (v.name || "").toLowerCase().includes(name) ||
        (v.email || "").toLowerCase().includes(name)
      );
    }
    return false;
  };

  if (Array.isArray(fieldVal)) {
    if (fieldVal.some(check)) return true;
    // Handle {users:[...]} wrapper
    for (const item of fieldVal) {
      if (item?.users && item.users.some(check)) return true;
    }
    return false;
  }
  if (fieldVal?.users) return fieldVal.users.some(check);
  return check(fieldVal);
}

// ── Cell value renderer (plain text) ─────────────────────
export function cellText(val) {
  if (val === null || val === undefined) return "";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (typeof val === "number") {
    if (val > 946684800000 && val < 4102444800000) return formatDate(val);
    return String(val);
  }
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    return val.map((v) => {
      if (typeof v === "string") return v;
      if (typeof v === "boolean") return v ? "Yes" : "No";
      return v?.en_name || v?.name || v?.text || v?.email || "";
    }).filter(Boolean).join(", ");
  }
  if (typeof val === "object") {
    if (val.users) return val.users.map((u) => u.en_name || u.name).join(", ");
    const str = val.name || val.en_name || val.text || val.label || val.value;
    if (str) return String(str);
    return "";
  }
  return String(val);
}

// ── Get all unique employees from records ─────────────────
// ── Employee identity ─────────────────────────────────────
// The worker labels a user it cannot resolve as "Unknown User"
// (formatAdminRowToRecord / formatUserRowToRecord). That is a placeholder, not
// a person, so it must never become an employee row — otherwise it collects
// every unresolved task into one bogus aggregate.
const PLACEHOLDER_NAMES = new Set(["unknown user", "unknown", "unknown employee", "—", "-", "n/a", ""]);

export function isPlaceholderName(name) {
  return PLACEHOLDER_NAMES.has(String(name || "").trim().toLowerCase());
}

// Does this user-task row belong to `emp`?
//
// Matching used to be `name.includes(empName)`, which was wrong twice over:
// the "Unknown User" placeholder swallowed every unresolved row, and a short
// name ("Ram") matched a longer one ("Ramesh"). Rows carry `user_id`, so match
// on that first; exact name is kept as a fallback for rows missing an id.
export function taskBelongsToEmployee(record, emp) {
  if (!record || !emp) return false;

  const empId = emp.id ? String(emp.id) : "";
  const rowUserId = record.user_id ? String(record.user_id) : "";
  if (empId && rowUserId && empId === rowUserId) return true;

  const target = String(emp.name || "").trim().toLowerCase();
  if (!target || isPlaceholderName(target)) return false;

  const f = record.fields || {};
  const val = f["User"] || f["Assigned Users"] || f["Total Assigned Users"];
  const names = [];
  const collect = (v) => {
    if (!v) return;
    if (typeof v === "string") {
      v.split(",").forEach(s => names.push(s.trim().toLowerCase()));
      return;
    }
    if (typeof v === "object") {
      // An id match here is as authoritative as the row-level one.
      if (empId && (v.id || v.open_id) && String(v.id || v.open_id) === empId) names.push(target);
      const n = v.en_name || v.name;
      if (n) names.push(String(n).trim().toLowerCase());
    }
  };
  if (Array.isArray(val)) val.forEach(collect); else collect(val);
  return names.includes(target);
}

// ── Active (non-deleted) Lark users ───────────────────────
// The D1 `lark_users` table is rebuilt from the live Lark directory on every
// sync, so a user deleted in Lark drops out of it. That makes it the authority
// on who still exists — task history does NOT, because an old task keeps the
// name of whoever it was assigned to forever.
//
// Returns null when the list hasn't loaded, which callers treat as "don't
// filter". That way a slow or failed /api/users never blanks out every
// dropdown in the app.
export function buildActiveUserIndex(larkUsers) {
  if (!Array.isArray(larkUsers) || larkUsers.length === 0) return null;
  const ids = new Set(), names = new Set();
  for (const u of larkUsers) {
    if (u?.id) ids.add(String(u.id));
    const n = (u?.name || u?.en_name || "").trim().toLowerCase();
    if (n) names.add(n);
  }
  return { ids, names };
}

// True when this person still exists in Lark. Matches on open_id first so a
// rename doesn't drop someone; only falls back to name for legacy records that
// never stored an id. A record that HAS an id which is absent from the
// directory is a deleted user.
export function isActiveUser(index, person) {
  if (!index) return true;
  const id = person?.id || person?.open_id || "";
  if (id) return index.ids.has(String(id));
  const name = (person?.name || person?.en_name || "").trim().toLowerCase();
  return name ? index.names.has(name) : false;
}

// Index departed users by open_id and by lower-cased name, so a person can be
// matched back to the date they were deleted in Lark.
export function buildDeletedUserIndex(deletedUsers) {
  const byId = new Map(), byName = new Map();
  for (const u of (deletedUsers || [])) {
    if (!u?.deleted_at) continue;
    const at = Number(u.deleted_at) || 0;
    if (u.id) byId.set(String(u.id), at);
    const n = (u.name || u.en_name || "").trim().toLowerCase();
    if (n && !byName.has(n)) byName.set(n, at);
  }
  return { byId, byName };
}

// When was this person deleted in Lark? 0 means "still here".
export function deletedAtFor(deletedIndex, person) {
  if (!deletedIndex) return 0;
  const id = person?.id || person?.open_id || "";
  if (id && deletedIndex.byId.has(String(id))) return deletedIndex.byId.get(String(id));
  const name = (person?.name || person?.en_name || "").trim().toLowerCase();
  if (name && deletedIndex.byName.has(name)) return deletedIndex.byName.get(name);
  return 0;
}

// Build the employee list for reports and people pages.
//
// opts:
//   larkUsers        — the active roster; anyone absent from it is treated as gone
//   deletedUsers     — departed people with their `deleted_at` timestamps
//   periodStartMs    — start of the selected range, or null for "all time"
//   includeAllActive — seed the list with EVERY current Lark member, so someone
//                      with no tasks assigned still gets a row (with zeros)
//                      instead of being missing from the report entirely
//
// A departed employee is kept when the range covers time they actually worked:
// with no start date the range is all-time, so they are always included; with a
// start date they are dropped once that date is on or after their departure.
// Called with no opts it behaves exactly as before — no filtering, history only.
export function extractEmployees(adminRecords, opts) {
  const { larkUsers, deletedUsers, periodStartMs, includeAllActive } = opts || {};
  const activeIndex = buildActiveUserIndex(larkUsers);
  const deletedIndex = deletedUsers ? buildDeletedUserIndex(deletedUsers) : null;

  const map = {};

  // Seed with the whole active roster. Task history alone only ever surfaces
  // people who have been assigned something, which silently hides current
  // employees who happen to have no tasks.
  if (includeAllActive && Array.isArray(larkUsers)) {
    for (const u of larkUsers) {
      const name = u?.name || u?.en_name;
      if (!name || isPlaceholderName(name)) continue;
      if (!map[name]) map[name] = { name, email: u.email || "", id: u.id || "" };
    }
  }

  for (const rec of adminRecords) {
    const users = rec.fields?.["Assigned Users"] || rec.fields?.["Assigned By"];
    const process = (v) => {
      if (!v) return;
      const name = v.en_name || v.name;
      if (!name) return;
      // "Unknown User" is the worker's fallback label for an id it could not
      // resolve. Letting it through created a single row that matched every
      // unresolved task.
      if (isPlaceholderName(name)) return;

      if (!isActiveUser(activeIndex, v)) {
        // Gone from Lark. Hidden by default — an unfiltered report is about the
        // current team. They resurface only when the selected range reaches
        // back into the time they were still employed.
        if (!periodStartMs) return;
        const goneAt = deletedAtFor(deletedIndex, { id: v.id || v.open_id, name });
        // Unknown departure date (never tracked) → treat as gone, so a dated
        // report can't silently include someone we can't place in time.
        if (!goneAt || periodStartMs >= goneAt) return;
      }

      if (!map[name]) map[name] = { name, email: v.email, id: v.id || v.open_id || "" };
    };
    if (Array.isArray(users)) users.forEach(process);
    else if (users?.users) users.users.forEach(process);
    else process(users);
  }
  return Object.values(map);
}

// ── Performance Calculation ──────────────────────────────
export const normalizeToMs = (val) => {
  if (!val) return 0;
  let ms = 0;
  if (typeof val === "number") ms = val;
  else if (typeof val === "object" && val?.value) ms = val.value;
  else if (Array.isArray(val) && val[0]?.value) ms = val[0].value;
  else {
    const d = new Date(val);
    ms = isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ms <= 0) return 0;
  // Handle Excel serial numbers (Lark specific)
  if (ms < 1000000) { 
     ms = (ms - 25569) * 86400 * 1000;
  }
  return ms;
};

export function calculatePerformanceRating(tasks, adminRecords = []) {
  if (!tasks || tasks.length === 0) return 0;

  // Build admin-task lookup: a user-task only counts as "done" when
  // ALL assigned users have completed (admin status = "✅ Done").
  const adminDoneMap = {};        // taskId → boolean
  const adminCompletionMap = {};   // taskId → ms timestamp
  for (const ar of adminRecords) {
    const af = ar.fields || {};
    const tid = cellText(af["Task ID"]);
    if (!tid || tid === "—") continue;
    const st = cellText(af["Overall Status"]).toLowerCase();
    adminDoneMap[tid] = st.includes("done") || st.includes("✅");
    adminCompletionMap[tid] = normalizeToMs(af["Completed At"] || af["Completion Time"]);
  }
  const useAdminCheck = adminRecords.length > 0;

  const now = Date.now();
  let total = tasks.length;
  let pendingCount = 0;
  let overdueCount = 0;
  let withinTimeCount = 0;
  let afterTimeCount = 0;
  let lateDaysTotal = 0;
  let overdueDaysTotal = 0;

  tasks.forEach(task => {
    const f = task.fields || {};
    const taskIdStr = cellText(f["Task ID"]);

    // Only mark done when the WHOLE task is completed (all users finished)
    const isDone = useAdminCheck && taskIdStr
      ? !!adminDoneMap[taskIdStr]
      : !!f["My Completion"];

    const dueTime = normalizeToMs(f["End Date"]);

    // Use admin completion time (= when last user finished) when available
    const completionTime = (useAdminCheck && taskIdStr && adminCompletionMap[taskIdStr])
      ? adminCompletionMap[taskIdStr]
      : normalizeToMs(f["Completed At"] || f["Completion Time"]);

    if (!isDone) {
      if (dueTime > now || dueTime === 0) {
        pendingCount++;
      } else {
        overdueCount++;
        overdueDaysTotal += (now - dueTime) / (1000 * 60 * 60 * 24);
      }
    } else {
      // 1 minute grace period for on-time completion
      if (completionTime <= (dueTime + 60000) || dueTime === 0) {
        withinTimeCount++;
      } else {
        afterTimeCount++;
        lateDaysTotal += (completionTime - dueTime) / (1000 * 60 * 60 * 24);
      }
    }
  });

  const denom = total - pendingCount + overdueCount;
  if (denom <= 0) return 0;

  const scoreWithin = (withinTimeCount / denom) * 10;
  const scoreAfter = (afterTimeCount / denom) * 10;
  const penaltyLate = afterTimeCount > 0 ? (0.1 * (lateDaysTotal / afterTimeCount)) : 0;
  const penaltyOverdue = overdueCount > 0 ? (0.1 * (overdueDaysTotal / overdueCount)) : 0;

  let finalScore = (scoreWithin + scoreAfter - penaltyLate) - penaltyOverdue;

  return Math.max(0, Math.min(10, finalScore));
}
